import { and, asc, eq } from "drizzle-orm";
import { combo, combo_item, type PowerSyncDrizzleDB, product_variant } from "@comandero/powersync";
import { liveComboFilters, liveComboItemFilters, liveProductVariantFilters } from "../live/filters";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { generateId } from "@fleet/utils";

// ─── Product variants (sizes / priced options) ───────────────────────────────

/** Variantes de un producto (S/M/L), ordenadas. `price` es ABSOLUTO en centavos. */
export function getVariantsQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db
    .select()
    .from(product_variant)
    .where(and(eq(product_variant.product_id, productId), ...liveProductVariantFilters()))
    .orderBy(asc(product_variant.sort_order), asc(product_variant.name));
}

/** Creates variant with absolute price (cents). CC-7: awaited write. */
export async function createVariantMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  productId: string,
  name: string,
  price: number,
  opts?: { sortOrder?: number },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(product_variant).values({
    id,
    organization_id: organizationId,
    product_id: productId,
    name: name.trim(),
    price: Math.trunc(price),
    sort_order: opts?.sortOrder ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteVariantMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    product_variant,
    product_variant.id,
    id,
    { deleted_at: nowISO() },
    options,
  );
}

// ─── Combos / bundle menus at special price ──────────────────────────────────

export function getCombosQuery(db: PowerSyncDrizzleDB, organizationId: string) {
  return db
    .select()
    .from(combo)
    .where(and(eq(combo.organization_id, organizationId), ...liveComboFilters()))
    .orderBy(asc(combo.name));
}

export function getComboItemsQuery(db: PowerSyncDrizzleDB, comboId: string) {
  return db
    .select()
    .from(combo_item)
    .where(and(eq(combo_item.combo_id, comboId), ...liveComboItemFilters()));
}

/** Creates fixed-price combo (cents). CC-7: write with await. */
export async function createComboMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  price: number,
  opts?: { taxRate?: number },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(combo).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    price: Math.trunc(price),
    tax_rate: opts?.taxRate ?? 0,
    is_available: 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteComboMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, combo, combo.id, id, { deleted_at: nowISO() }, options);
}

/** Adds product to combo (with quantity). */
export async function addComboItemMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  comboId: string,
  productId: string,
  qty = 1,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(combo_item).values({
    id,
    organization_id: organizationId,
    combo_id: comboId,
    product_id: productId,
    qty: Math.max(1, Math.trunc(qty)),
    created_at: now,
    updated_at: now,
  });
  return id;
}
